import { useState } from "react";
import { AnchorText, Body1, Modal, SelectionGroup, TextSpan, XStack, YStack } from "@medbillai/ui";
import { OnboardingScreen } from "../../components/OnboardingScreen";
import { type OnDone, tagged } from "../../lib/flow-engine";
export type HouseholdSizeOutcomes = {
  selected: {
    householdSize: number;
  };
  back: {};
};
const Outcome = tagged<HouseholdSizeOutcomes>();
const MAX_HOUSEHOLD_SIZE = 8;
const HOUSEHOLD_SIZES = Array.from({
  length: MAX_HOUSEHOLD_SIZE
}, (_, i) => i + 1);
function BulletItem({
  children
}: {
  children: React.ReactNode;
}) {
  return <XStack gap="$sp1" data-sentry-element="XStack" data-sentry-component="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
      <Body1 data-sentry-element="Body1" data-sentry-source-file="HouseholdSize.tsx">•</Body1>
      <Body1 flexShrink={1} data-sentry-element="Body1" data-sentry-source-file="HouseholdSize.tsx">{children}</Body1>
    </XStack>;
}
export function HouseholdSize({
  onDone
}: {
  onDone: OnDone<HouseholdSizeOutcomes>;
}) {
  const [showWhoToCount, setShowWhoToCount] = useState(false);
  return <OnboardingScreen data-sentry-element="OnboardingScreen" data-sentry-component="HouseholdSize" data-sentry-source-file="HouseholdSize.tsx">
      <OnboardingScreen.Nav onBackPress={() => onDone(Outcome.back())} showLogo={false} data-sentry-element="OnboardingScreen.Nav" data-sentry-source-file="HouseholdSize.tsx" />
      <OnboardingScreen.Content data-sentry-element="OnboardingScreen.Content" data-sentry-source-file="HouseholdSize.tsx">
        <OnboardingScreen.Header alignment="left" title="How many people are in your household?" subtitle="Include yourself, your spouse, and anyone you claim as a dependent on your taxes." data-sentry-element="OnboardingScreen.Header" data-sentry-source-file="HouseholdSize.tsx" />
        <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="HouseholdSize.tsx">
          <SelectionGroup mode="radio" label="Household size" tone="brand" columns={4} value="" onChange={value => onDone(Outcome.selected({
          householdSize: Number(value)
        }))} data-sentry-element="SelectionGroup" data-sentry-source-file="HouseholdSize.tsx">
            {HOUSEHOLD_SIZES.map(size => <SelectionGroup.Tile key={size} prominent value={String(size)} label={size === MAX_HOUSEHOLD_SIZE ? `${size}+` : String(size)} />)}
          </SelectionGroup>
          <XStack justifyContent="center" paddingVertical="$sp3" data-sentry-element="XStack" data-sentry-source-file="HouseholdSize.tsx">
            <AnchorText testID="household.who-to-count" color="$textBrand" textAlign="center" textDecorationLine="underline"
          // An <a> without href gets no pointer from the browser; this
          // anchor is onPress-only, so opt in explicitly.
          cursor="pointer" onPress={() => setShowWhoToCount(true)} data-sentry-element="AnchorText" data-sentry-source-file="HouseholdSize.tsx">
              Not sure who to count?
            </AnchorText>
          </XStack>
        </YStack>
      </OnboardingScreen.Content>
      <Modal open={showWhoToCount} onOpenChange={setShowWhoToCount} headerProps={{
      title: "Who counts as your household"
    }} footerProps={{
      primaryButtonProps: {
        children: "Got it",
        onPress: () => setShowWhoToCount(false)
      }
    }} data-sentry-element="Modal" data-sentry-source-file="HouseholdSize.tsx">
        <YStack gap="$sp2" data-sentry-element="YStack" data-sentry-source-file="HouseholdSize.tsx">
          <Body1 data-sentry-element="Body1" data-sentry-source-file="HouseholdSize.tsx">
            Your household is everyone you'd list on your tax return:
          </Body1>
          <YStack gap="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="HouseholdSize.tsx">
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Yourself
              </TextSpan>
            </BulletItem>
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Your spouse,
              </TextSpan>{" "}
              if you file taxes together
            </BulletItem>
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Anyone you claim as a dependent,
              </TextSpan>{" "}
              like children or relatives you support
            </BulletItem>
          </YStack>
          <Body1 data-sentry-element="Body1" data-sentry-source-file="HouseholdSize.tsx">A few things people often wonder about:</Body1>
          <YStack gap="$sp0.5" data-sentry-element="YStack" data-sentry-source-file="HouseholdSize.tsx">
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Count dependents who live elsewhere,
              </TextSpan>{" "}
              as long as you claim them
            </BulletItem>
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Count a baby on the way,
              </TextSpan>{" "}
              if you're expecting
            </BulletItem>
            <BulletItem data-sentry-element="BulletItem" data-sentry-source-file="HouseholdSize.tsx">
              <TextSpan bold unstyled data-sentry-element="TextSpan" data-sentry-source-file="HouseholdSize.tsx">
                Don't count roommates, unmarried partners, or other adults
              </TextSpan>{" "}
              you don't claim
            </BulletItem>
          </YStack>
          <Body1 data-sentry-element="Body1" data-sentry-source-file="HouseholdSize.tsx">
            We use this number to estimate your eligibility for income-based
            discounts, so the more accurate it is, the better your estimate.
          </Body1>
        </YStack>
      </Modal>
    </OnboardingScreen>;
}